/**
 * What becomes a recording to send (UC-8, light): what the browser's recorder ended with, or an
 * audio file that exists (a dictaphone or voice-memo recording). Either is judged before a byte
 * of it is sent — audio in a container the server takes, not empty, at most 95 MiB — and refused
 * with its own words otherwise. Pure.
 */
import type { RecordingFileProblem } from '@/components/humint/questioning/recording-types'
import { MAX_RECORDING_BYTES } from '@/services/api/humint-recordings'
import type { RecordedAudio } from './recorder-controller'
import { uploadExtension } from './recorder-format'
import type { RecordingSource } from './recording-upload'

export type RecordingIntake =
  | { kind: 'accepted'; source: RecordingSource }
  | { kind: 'refused'; refusal: RecordingFileProblem }

const refused = (refusal: RecordingFileProblem): RecordingIntake => ({ kind: 'refused', refusal })

/**
 * Judge what is offered and take it under the extension its container names. An audio type the
 * server has no container for is told apart from a file that is no audio at all.
 */
function judge(
  offered: { name: string; type: string; size: number },
  source: Omit<RecordingSource, 'extension'>
): RecordingIntake {
  const extension = uploadExtension(offered)
  if (!extension) {
    const audio = offered.type.trim().toLowerCase().startsWith('audio/')
    return refused(audio ? 'unsupported_format' : 'not_audio')
  }
  if (offered.size === 0) return refused('empty')
  if (offered.size > MAX_RECORDING_BYTES) return refused('too_large')
  return { kind: 'accepted', source: { ...source, extension } }
}

/** What the recorder ended with: it started when the recorder did, and has its recorded length. */
export function intakeOfRecorded(audio: RecordedAudio): RecordingIntake {
  const { blob, startedAt, durationMs, interrupted } = audio
  return judge(
    { name: '', type: blob.type, size: blob.size },
    { blob, capturedAt: startedAt, durationMs, interrupted }
  )
}

/**
 * A file picked from the device. When it was recorded is not known, so it is captured now; its
 * length is known once it is transcribed.
 */
export function intakeOfFile(file: File, now: Date = new Date()): RecordingIntake {
  return judge(file, {
    blob: file,
    capturedAt: now.toISOString(),
    durationMs: null,
    interrupted: false,
  })
}
